"use client";
import React, { createContext, useContext, ReactNode } from 'react';
import { HomePageData } from '@/types/homePage';
import { homePageData } from '@/data/homePageData';

// Context
const HomeDataContext = createContext<HomePageData>(homePageData);

// Provider component
interface HomeDataProviderProps {
  children: ReactNode;
  data?: Partial<HomePageData>;
}

export const HomeDataProvider: React.FC<HomeDataProviderProps> = ({ 
  children, 
  data = {} 
}) => {
  const mergedData = {
    ...homePageData,
    ...data,
    hero: { ...homePageData.hero, ...data.hero },
    about: { ...homePageData.about, ...data.about },
    topBar: { ...homePageData.topBar, ...data.topBar },
    navBar: { ...homePageData.navBar, ...data.navBar },
    creativeTeam: { ...homePageData.creativeTeam, ...data.creativeTeam },
    latestService: { ...homePageData.latestService, ...data.latestService },
    askQuestion: { ...homePageData.askQuestion, ...data.askQuestion },
    brandsSlider: { ...homePageData.brandsSlider, ...data.brandsSlider },
    clientsReviews: { ...homePageData.clientsReviews, ...data.clientsReviews },
    gallery: { ...homePageData.gallery, ...data.gallery },
    blog: { ...homePageData.blog, ...data.blog },
    contactUs: { ...homePageData.contactUs, ...data.contactUs },
    footer: { ...homePageData.footer, ...data.footer },
    numberTalks: { ...homePageData.numberTalks, ...data.numberTalks },
    workProcess: { ...homePageData.workProcess, ...data.workProcess },
    digitalSolutions: { ...homePageData.digitalSolutions, ...data.digitalSolutions },
  };

  return (
    <HomeDataContext.Provider value={mergedData}>
      {children}
    </HomeDataContext.Provider>
  );
};

// Custom hook to use the context
export const useHomeData = (): HomePageData => {
  const context = useContext(HomeDataContext);
  if (!context) {
    throw new Error('useHomeData must be used within a HomeDataProvider');
  }
  return context;
};

// Individual section hooks for better performance
export const useHeroData = () => {
  const { hero } = useHomeData();
  return hero;
};

export const useAboutData = () => {
  const { about } = useHomeData();
  return about;
};

export const useTopBarData = () => {
  const { topBar } = useHomeData();
  return topBar;
};

export const useNavBarData = () => {
  const { navBar } = useHomeData();
  return navBar;
};

export const useCreativeTeamData = () => {
  const { creativeTeam } = useHomeData();
  return creativeTeam;
};

export const useGalleryData = () => {
  const { gallery } = useHomeData();
  return gallery;
};

export const useContactUsData = () => {
  const { contactUs } = useHomeData();
  return contactUs;
};

export const useFooterData = () => {
  const { footer } = useHomeData();
  return footer;
};

export const useBlogData = () => {
  const { blog } = useHomeData();
  return blog;
};

export const useLatestServiceData = () => {
  const { latestService } = useHomeData();
  return latestService;
};

export const useAskQuestionData = () => {
  const { askQuestion } = useHomeData();
  return askQuestion;
};

export const useBrandsSliderData = () => {
  const { brandsSlider } = useHomeData();
  return brandsSlider;
};

export const useClientsReviewsData = () => {
  const { clientsReviews } = useHomeData();
  return clientsReviews;
};

export const useNumberTalksData = () => {
  const { numberTalks } = useHomeData();
  return numberTalks;
};

export const useWorkProcessData = () => {
  const { workProcess } = useHomeData();
  return workProcess;
};

export const useDigitalSolutionsData = () => {
  const { digitalSolutions } = useHomeData();
  return digitalSolutions;
};
